package com.vidaensupalabra.vsp.datos

import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.engine.js.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.ContentType
import io.ktor.http.contentType
import kotlinx.coroutines.CancellationException
import io.ktor.serialization.kotlinx.json.*
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

@Serializable
data class ComunidadComentario(
    val userId: String = "",
    val autor: String = "",
    val autorFoto: String? = null,
    val texto: String = "",
    val timestamp: Long = 0L,
    val fecha: String = "",
    val esAdmin: Boolean = false
)

@Serializable
data class ComunidadPost(
    val id: String = "", // Firebase generará esto automáticamente
    val userId: String = "",
    val autor: String = "",
    val autorFoto: String? = null,
    val timestamp: Long = 0L, // Usaremos esto para ordenar los posts
    val fecha: String = "",
    val titulo: String = "",
    val contenido: String = "",
    val youtubeVideoId: String? = null,
    val imagenUrl: String? = null,
    val linkPreviewUrl: String? = null,
    val linkPreviewTitle: String? = null,
    val linkPreviewDesc: String? = null,
    val linkPreviewImage: String? = null,
    val amens: Map<String, Boolean> = emptyMap(),
    val comentarios: Map<String, ComunidadComentario> = emptyMap(),
    val esAdmin: Boolean = false
)

object ComunidadRepository {
    private val client = HttpClient(Js) {
        install(ContentNegotiation) {
            json(Json { ignoreUnknownKeys = true })
        }
    }
    
    // Tu URL de Realtime Database base
    private const val FIREBASE_BASE_URL = "https://vidaensupalabra-830e4-default-rtdb.firebaseio.com/comunidad_posts"
    private const val FIREBASE_USERS_URL = "https://vidaensupalabra-830e4-default-rtdb.firebaseio.com/usuarios"

    suspend fun getPosts(limit: Int = 10, endAtTimestamp: Long? = null): List<ComunidadPost> {
        return try {
            val responseText = client.get("$FIREBASE_BASE_URL.json") {
                parameter("orderBy", "\"timestamp\"")
                parameter("limitToLast", limit)
                if (endAtTimestamp != null) parameter("endAt", endAtTimestamp)
            }.bodyAsText()
            if (responseText == "null" || responseText.isBlank()) return emptyList()
            
            // Firebase devuelve un Map<String, Object> donde la Key es el ID autogenerado
            val map = Json { ignoreUnknownKeys = true }.decodeFromString<Map<String, ComunidadPost>>(responseText)
            map.map { (key, post) -> post.copy(id = key) }.sortedByDescending { it.timestamp }
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            e.printStackTrace()
            emptyList()
        }
    }

    suspend fun getPostById(id: String): ComunidadPost? {
        return try {
            val responseText = client.get("$FIREBASE_BASE_URL/$id.json").bodyAsText()
            if (responseText == "null" || responseText.isBlank()) return null
            
            val post = Json { ignoreUnknownKeys = true }.decodeFromString<ComunidadPost>(responseText)
            post.copy(id = id)
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            e.printStackTrace()
            null
        }
    }

    suspend fun getPostsByUser(uid: String): List<ComunidadPost> {
        return try {
            val responseText = client.get("$FIREBASE_BASE_URL.json") {
                parameter("orderBy", "\"userId\"")
                parameter("equalTo", "\"$uid\"")
            }.bodyAsText()
            if (responseText == "null" || responseText.isBlank()) return emptyList()
            
            val map = Json { ignoreUnknownKeys = true }.decodeFromString<Map<String, ComunidadPost>>(responseText)
            map.map { (key, post) -> post.copy(id = key) }.sortedByDescending { it.timestamp }
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            e.printStackTrace()
            emptyList()
        }
    }

    suspend fun createPost(post: ComunidadPost): Boolean {
        return try {
            val response = client.post("$FIREBASE_BASE_URL.json") {
                contentType(ContentType.Application.Json)
                setBody(post)
            }
            response.status.value in 200..299
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            e.printStackTrace()
            false
        }
    }

    suspend fun updatePost(post: ComunidadPost): Boolean {
        return try {
            val response = client.put("$FIREBASE_BASE_URL/${post.id}.json") {
                contentType(ContentType.Application.Json)
                setBody(post)
            }
            response.status.value in 200..299
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            e.printStackTrace()
            false
        }
    }

    suspend fun deletePost(id: String): Boolean {
        return try {
            val response = client.delete("$FIREBASE_BASE_URL/$id.json")
            response.status.value in 200..299
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            e.printStackTrace()
            false
        }
    }

    suspend fun toggleAmen(postId: String, userId: String, isAmen: Boolean): Boolean {
        return try {
            val url = "$FIREBASE_BASE_URL/$postId/amens/$userId.json"
            if (isAmen) {
                client.put(url) {
                    contentType(ContentType.Application.Json)
                    setBody(true)
                }
            } else {
                client.delete(url)
            }
            true
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            e.printStackTrace()
            false
        }
    }

    suspend fun addComentario(postId: String, commentId: String, comentario: ComunidadComentario): Boolean {
        return try {
            client.put("$FIREBASE_BASE_URL/$postId/comentarios/$commentId.json") {
                contentType(ContentType.Application.Json)
                setBody(comentario)
            }
            true
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            e.printStackTrace()
            false
        }
    }

    suspend fun deleteComentario(postId: String, commentId: String): Boolean {
        return try {
            val response = client.delete("$FIREBASE_BASE_URL/$postId/comentarios/$commentId.json")
            response.status.value in 200..299
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            e.printStackTrace()
            false
        }
    }

    suspend fun checkUserNeedsBirthdate(uid: String): Boolean {
        return try {
            val response = client.get("$FIREBASE_USERS_URL/$uid/fechaNacimiento.json").bodyAsText()
            response == "null" || response.isBlank()
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            e.printStackTrace()
            false // Ante la duda o error de red, no bloqueamos la UI
        }
    }

    suspend fun saveUserBirthdate(uid: String, date: String): Boolean {
        return try {
            val response = client.put("$FIREBASE_USERS_URL/$uid/fechaNacimiento.json") {
                contentType(ContentType.Application.Json)
                setBody("\"$date\"") // Serializado como String JSON
            }
            response.status.value in 200..299
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            e.printStackTrace()
            false
        }
    }
}